import { createActionError } from "@/client-error";
import i18n from "i18next";
import { createApiClient } from "@/clients/api.client";
import { createDesktopClient } from "@/clients/desktop.client";
import { createDatadogClient } from "@/clients/datadog.client";
import { createRevenueCatClient } from "@/clients/revenuecat.client";
import { createRuntimeClient } from "@/clients/runtime.client";
import { createScanClient } from "@/clients/scan.client";
import { createReviewClient } from "@/clients/review.client";
import { createFirebaseClient } from "@/clients/firebase.client";
import { createRemoteFileClient } from "@/clients/remote-file.client";
import { createUploadClient } from "@/clients/upload.client";
import { createFpixelClient } from "@/clients/fpixel.client";
import { createGtagClient } from "@/clients/gtag.client";
import { createActions } from "@/actions";
import { loadConfig } from "@/config";
import {
  configureAuthActions,
  configureAuthStore,
  useAuth,
} from "@/models/useAuth";
import { createAppStore } from "@/store";
import { configureTelemetryFacade, createTelemetry } from "@/telemetry";
import { createLifecycle } from "@/lifecycle";
import {
  captureAttribution,
  getAttributionChannel,
  getStoredAttribution,
} from "@/attribution";
import {
  configureLiveRecorder,
  configureLiveRecorderServerUrl,
  type LiveRecorderHook,
} from "@/recording/useLiveRecorder";
import { useLiveRecorderAndroid } from "@/recording/useLiveRecorderAndroid";
import { useLiveRecorderIOS } from "@/recording/useLiveRecorderIOS";
import {
  configureWebRecordingApi,
  useLiveRecorderWeb,
} from "@/recording/useLiveRecorderWeb";
import { version } from "../package.json";
import { configureFeatureAdoptionStore } from "@/models/feature-adoption";
import { configurePlatformRuntime } from "@/platform";
import {
  configureDictationLanguageSource,
  configureDictationVocabularySource,
} from "@/models/useGlobalDictation";
import { composeDictationVocabularyRules } from "@/dictation-interactions";
import { configureLiveRecordingRecoveryStorage } from "@/recording/recovery";
import { configureRecordingPorts } from "@/recording/ports";
import {
  DesktopAudioPermissionError,
  openDesktopMeetingAudio,
} from "@/recording/desktop-audio";
import { configureNativeCapabilities } from "@/models/useNative";
import { useUISlice } from "@/models/useUISlice";
import { configureFileDownloads } from "@/file-download";
import { getLocale } from "@/languages";
import { getExternalSubscriptionManagementUrl } from "@/product-links";

export const config = loadConfig();
export const desktop = createDesktopClient();
export const runtime = createRuntimeClient({
  desktopClipboardWrite: desktop.writeClipboard,
});
const appStore = createAppStore({
  platform: runtime.platform,
  storage: runtime.storage,
});
export const useAppStore = appStore.useStore;
export const remoteFiles = createRemoteFileClient({
  defaultTimeoutMs: config.limits.defaultApiTimeoutMs,
});
configureFileDownloads(remoteFiles);
export const uploads = createUploadClient({
  noProgressTimeoutMs: config.limits.uploadNoProgressTimeoutMs,
});
configurePlatformRuntime(runtime);
configureLiveRecordingRecoveryStorage(runtime.storage);
configureFeatureAdoptionStore(useAppStore);
configureDictationVocabularySource(() =>
  composeDictationVocabularyRules(useAppStore.getState().dictationVocabulary),
);
configureDictationLanguageSource(
  () => useAppStore.getState().userData?.preferences?.dictationLanguage,
  () => useAppStore.getState().userData?.preferences?.recentDictationLanguages,
);
export const scan = createScanClient();
export const review = createReviewClient();
export const firebase = createFirebaseClient();
configureNativeCapabilities(runtime, review);

configureAuthStore({
  getState: () => useAppStore.getState(),
});

let useRecording: () => LiveRecorderHook;
switch (runtime.platform) {
  case "web":
    useRecording = useLiveRecorderWeb;
    break;
  case "ios":
    useRecording = useLiveRecorderIOS;
    break;
  case "android":
    useRecording = useLiveRecorderAndroid;
    break;
}
configureLiveRecorder(useRecording);
configureLiveRecorderServerUrl(config.apiUrl);
export { useRecording };

export const api = createApiClient({
  baseUrl: config.apiUrl,
  defaultTimeoutMs: config.limits.defaultApiTimeoutMs,
  getToken: () => useAppStore.getState().userToken,
  onUnauthorized: () => useAppStore.getState().expireSession(),
});

export const revenueCat = createRevenueCatClient();

const datadog = createDatadogClient({
  clientToken: config.telemetry.datadogClientToken,
  environment: config.telemetry.environment,
  service:
    runtime.platform === "web" ? "1transcribe-web" : "1transcribe-native",
  version,
});
const canSendMetaEvents = () =>
  runtime.platform === "web" &&
  window.location.hostname === "app.1transcribe.com";
export const currentAcquisitionAttribution = (): Record<string, string> =>
  captureAttribution({ includeMeta: canSendMetaEvents() });
const fpixel = createFpixelClient(config.facebookPixelId, {
  canTrack: canSendMetaEvents,
});
const gtag = createGtagClient(config.googleAnalyticsId);

let desktopTelemetryContext:
  | { desktopOs: "macos" | "windows"; desktopVersion: string }
  | undefined;

export const telemetry = createTelemetry({
  datadog,
  firebase: runtime.isNative ? firebase : undefined,
  fpixel,
  gtag,
  getFirebaseProperties: () => ({ language: getLocale(), version }),
  getAttribution:
    runtime.platform === "web"
      ? () => {
          const attribution = captureAttribution({
            includeMeta: canSendMetaEvents(),
          });
          return { ...attribution, channel: getAttributionChannel(attribution) };
        }
      : undefined,
  getContext: () => {
    const state = useAppStore.getState();
    return {
      platform: desktop.isAvailable ? "desktop" : runtime.platform,
      isSubscribed: Boolean(state.isSubscribed),
      ...desktopTelemetryContext,
      country: state.country || undefined,
      appLanguage: i18n.language || undefined,
      onboardingPersona: state.onboardingPersona || undefined,
      onboardingIntent: state.onboardingIntent || undefined,
      onboardingUseCases: [...state.onboardingUseCases],
    };
  },
});

configureTelemetryFacade(telemetry);

export const setDesktopTelemetryContext = (context: {
  desktopOs: "macos" | "windows";
  desktopVersion: string;
}): void => {
  desktopTelemetryContext = context;
  telemetry.refreshContext();
};
configureRecordingPorts({
  ...(desktop.isAvailable
    ? {
        openAudioInput: async () => {
          try {
            return await openDesktopMeetingAudio();
          } catch (error) {
            if (error instanceof DesktopAudioPermissionError)
              useUISlice.setState((state) => ({
                desktopAudioPermissionDenied: {
                  ...state.desktopAudioPermissionDenied,
                  [error.permission]: true,
                },
              }));
            throw error;
          }
        },
      }
    : {}),
  platform: runtime.platform,
  useUiSelector: (selector) => useUISlice(selector),
  getUiState: () => useUISlice.getState(),
  useAuthToken: () => useAppStore((state) => state.userToken),
  getAuthToken: () => useAppStore.getState().userToken,
  onAppStateChange: runtime.app.onStateChange,
  notifyNow: (notification) =>
    runtime.notifications.schedule([
      { ...notification, scheduleAt: new Date() },
    ]),
  reportStopped: async (transcriptId, finalSequenceNumber) => {
    const result = await actions.transcript.liveRecordingChunk({ action: "stopped", transcriptId, finalSequenceNumber });
    if ("error" in result) throw createActionError(result.error);
  },
  recordStatus: (status) => telemetry.track("recording.statusChanged", status),
});

// One iOS disclosure for AI text operations. The action saves the decision
// before allowing any feature request to leave the client.
const requestAiConsent = (signal?: AbortSignal): Promise<boolean> => {
  if (signal?.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    let finished = false;
    let unsubscribe = () => {};
    const finish = (accepted: boolean) => {
      if (finished) return;
      finished = true;
      unsubscribe();
      signal?.removeEventListener("abort", abort);
      if (useUISlice.getState().alertModal === modal) useUISlice.getState().setAlertModal(undefined);
      resolve(accepted);
    };
    const abort = () => finish(false);
    const modal = {
      title: i18n.t("accountPrivacy.aiTitle"),
      subtitle: i18n.t("accountPrivacy.aiBody"),
      cancelText: i18n.t("accountPrivacy.notNow"),
      privacyLink: true,
      onDismiss: () => finish(false),
      button: { text: i18n.t("accountPrivacy.agree"), onPress: () => finish(true) },
    };
    useUISlice.getState().setAlertModal(modal);
    unsubscribe = useUISlice.subscribe((state) => {
      if (state.alertModal !== modal) finish(false);
    });
    /* action-id: lifecycle.aiConsent.abort */ signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
  });
};

export const actions = createActions({
  api,
  requestReview: runtime.isNative ? () => review.request() : undefined,
  requestAiConsent: runtime.platform === "ios" ? requestAiConsent : undefined,
  revenueCat,
  store: {
    getState: () => useAppStore.getState(),
    // Zustand persist returns the durable storage write at runtime even
    // though its public setState type is void. Preserve it so session-ending
    // navigation can wait until the cleared identity is safely stored.
    setState: (update) =>
      useAppStore.setState(update) as unknown as Promise<void>,
    setSubscribed: (isSubscribed) => {
      useAppStore.getState().setIsSubscribed(isSubscribed);
    },
  },
  telemetry,
  clock: {
    now: () => Date.now(),
    setTimeout: (callback, timeoutMs) => setTimeout(callback, timeoutMs),
    clearTimeout: (handle) => clearTimeout(handle),
  },
  subscriptionEnvironment: {
    baseAppUrl: () =>
      desktop.isAvailable
        ? `${window.location.origin}/desktop-return?desktop_return=true`
        : window.location.href,
    attribution: () => {
      const value =
        runtime.platform === "web"
          ? captureAttribution({ includeMeta: canSendMetaEvents() })
          : getStoredAttribution();
      return Object.keys(value).length > 0 ? value : undefined;
    },
    managementUrl: getExternalSubscriptionManagementUrl,
    nativeConfiguration: async () => {
      const user = useAuth.currentAuthUser();
      let firebaseAppInstanceId: string | undefined;
      try {
        firebaseAppInstanceId = (await firebase.getInstanceId()) || undefined;
      } catch {
        // Optional attribution must not hide offerings or entitlement state.
      }
      return {
        appUserId: user?.userId,
        email: user?.email,
        apiKey:
          runtime.platform === "ios"
            ? config.purchases.iosApiKey
            : config.purchases.androidApiKey,
        firebaseAppInstanceId,
        enableAdServicesAttribution: runtime.platform === "ios",
      };
    },
  },
});

configureWebRecordingApi({
  liveRecordingChunk: actions.transcript.liveRecordingChunk,
});
configureAuthActions({
  session: actions.session,
  user: actions.user,
});

export const lifecycle = createLifecycle({
  actions,
  runtime,
  telemetry,
  checkoutStorage:
    runtime.platform === "web"
      ? {
          getItem: async (key) => window.localStorage.getItem(key),
          setItem: async (key, value) =>
            window.localStorage.setItem(key, value),
        }
      : runtime.storage,
});

export const hydrateApp = appStore.hydrate;
